/* eslint-disable @typescript-eslint/no-use-before-define */
import { AppNavigationProvider } from '@euler/app/components/AppNavigationProvider';
import { useStatusBarStyle } from '@euler/app/components/AppStatusBar';
import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { MeHomeScreen } from '@euler/app/flows/me/pages/MeHomeScreen';
import { NavHeaderBg } from '@euler/app/flows/settings/components/NavHeaderBg';
import { SettingsScreens } from '@euler/app/flows/settings/screens';
import { MultilineHeaderTitle } from '@euler/components/MultilineHeaderTitle';
import { ErrorFallback } from '@euler/components/error';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useIsDesktopLayout,
  useIsMobileLayout,
} from '@euler/functions/responsiveness';
import { ScreenToParams, spreadScreens } from '@euler/functions/spreadScreens';
import {
  WrappedNavigatorScreenContext,
  wrapNavigatorScreen,
} from '@euler/functions/wrapNavigatorScreen';
import { ScopedObservableSuspenseResourceContextProvider } from '@euler/utils/hooks';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import {
  TransitionPresets,
  createStackNavigator,
} from '@react-navigation/stack';
import * as Sentry from '@sentry/react';
import { memo, useMemo } from 'react';
import { StyleProp, ViewStyle, useWindowDimensions } from 'react-native';

const Stack = createStackNavigator();

// Settings and help are sub-pages of 我的 rather than destinations of their
// own, so their pages live in this stack and back returns to the Me home.
const Screens = {
  _home: MeHomeScreen,
  ...SettingsScreens,
};

export type MeNavParams = ScreenToParams<typeof Screens>;

type Props = {
  initialScreen?: keyof MeNavParams;
};

export const MeFlow = wrapNavigatorScreen(
  (props: Props) => {
    useStatusBarStyle('dark');
    return <Content {...props} />;
  },
  {
    title: '我的',
    headerShown: false,
  },
);

const Content = memo((props: Props) => {
  const isMobile = useIsMobileLayout();
  const isDesktop = useIsDesktopLayout();
  const { width } = useWindowDimensions();
  const { rpx } = useResponsiveContext();
  const contentContainerStyle = useMemo<StyleProp<ViewStyle> | undefined>(
    () =>
      isMobile
        ? undefined
        : {
            width: Math.min(width, 730),
            marginLeft: 'auto',
            marginRight: 'auto',
          },
    [isMobile, width],
  );
  return (
    <AppNavigationProvider>
      <WrappedNavigatorScreenContext.Provider value={{ contentContainerStyle }}>
        <LayoutProviderView fallbackToChildren style={{ flex: 1 }}>
          <Sentry.ErrorBoundary
            // @ts-ignore
            fallback={(params: any) => <ErrorFallback {...params} />}
          >
            <ScopedObservableSuspenseResourceContextProvider scope={'me'}>
              <BottomSheetModalProvider>
                <Stack.Navigator
                  initialRouteName={props.initialScreen ?? '_home'}
                  screenOptions={{
                    ...TransitionPresets.SlideFromRightIOS,
                    headerMode: 'float',
                    headerTitleAlign: isDesktop ? 'left' : 'center',
                    headerBackTitle: ' ',
                    headerTitleStyle: {
                      fontSize: rpx(isDesktop ? 20 : 18),
                      fontFamily: FontFamily.NotoSans.Bold,
                      color: isDesktop ? '#0f172a' : '#fff',
                    },
                    headerStyle: isDesktop
                      ? {
                          backgroundColor: '#fff',
                          borderBottomWidth: 1,
                          borderBottomColor: '#e5e7eb',
                          shadowOpacity: 0,
                          elevation: 0,
                        }
                      : undefined,
                    cardStyle: {
                      backgroundColor: '#f7f7f7',
                    },
                    headerTitle: MultilineHeaderTitle,
                    headerBackImage: () => (
                      <BackIcon fill={isDesktop ? '#0f172a' : '#fff'} />
                    ),
                    headerBackground: isDesktop ? undefined : NavHeaderBg,
                  }}
                >
                  {spreadScreens(
                    Stack.Screen,
                    Screens,
                    props.initialScreen ?? '_home',
                    {},
                  )}
                </Stack.Navigator>
              </BottomSheetModalProvider>
            </ScopedObservableSuspenseResourceContextProvider>
          </Sentry.ErrorBoundary>
        </LayoutProviderView>
      </WrappedNavigatorScreenContext.Provider>
    </AppNavigationProvider>
  );
});
